import { type StreamingStateManager } from '@databases/database-ai-first-creation-state';
import type {
  EditFieldSettingToken,
  FieldToken,
  RemoveFieldToken,
} from '@databases/streaming-csv-parser';
import {
  CsvErrorCategory,
  type CsvErrorCollector,
  CsvErrorType,
  TokenType,
} from '@databases/streaming-csv-parser';
import {
  type DatabaseFieldSettings,
  DatabaseFieldType,
  DEFAULT_ENTRY_ID,
} from '@databases/types/database';
import { createField } from '@databases/utils/database/utils';

import type { DatabaseAgent } from '../agents/database-agent';
import type { ProcessingError } from '../types';
import { logValidationErrors, stringToDatabaseFieldType } from '../utils';

import type { ValueProcessor } from './value-processor';

const DEPENDENT_FIELD_TYPES = new Set([
  DatabaseFieldType.PAGE_DETAILS,
  DatabaseFieldType.PAGE_STATUS,
  DatabaseFieldType.PAGE_LABELS,
  DatabaseFieldType.ENTRY_DETAILS,
  DatabaseFieldType.JIRA_ISSUE_DETAILS,
]);

const OUT_OF_SCOPE_FIELD_TYPES = new Set([
  DatabaseFieldType.ENTRY_LINK,
  DatabaseFieldType.ENTRY_DETAILS,
  DatabaseFieldType.JIRA_ISSUE_DETAILS,
]);

export class FieldProcessor {
  constructor(
    private readonly agent: DatabaseAgent,
    private readonly cloudId: string,
    private readonly streamingState: StreamingStateManager,
    private readonly valueProcessor: ValueProcessor,
    private readonly errorCollector?: CsvErrorCollector
  ) {}

  private addValidationError(error: ProcessingError): void {
    this.errorCollector?.addError(
      error.message,
      CsvErrorCategory.FIELD,
      CsvErrorType.VALIDATION
    );
  }

  private addValidationErrors(errors: ProcessingError[]): void {
    for (const error of errors) {
      this.addValidationError(error);
    }
  }

  public handleFieldToken(fieldToken: FieldToken): void {
    const errors = this.validateField(fieldToken);

    if (errors.length > 0) {
      const hasBlockingErrors = errors.some((error) => error.severity !== 'warning');

      logValidationErrors(errors);
      this.addValidationErrors(errors);

      if (hasBlockingErrors) {
        return;
      }
    }

    const fieldType = stringToDatabaseFieldType(fieldToken.type);
    if (!fieldType) {
      return;
    }

    const settings = this.buildFieldSettings(fieldType, fieldToken);
    const fieldData = { name: fieldToken.name || '' };

    const field = createField(fieldType, fieldData);
    this.streamingState.setFieldIdForProcessing(field.id);

    if (settings) {
      field.settings = { ...field.settings, ...settings };
    }
    this.streamingState.markFieldSeen(field.id);
    this.agent.addField(field);
  }

  public handleEditFieldSettingToken(
    editFieldSettingsToken: EditFieldSettingToken
  ): void {
    const fieldId = this.agent.getFieldIdByName(editFieldSettingsToken.fieldName);
    if (fieldId) {
      this.streamingState.setFieldIdForProcessing(fieldId);
      this.streamingState.markFieldSeen(fieldId);
    }

    switch (editFieldSettingsToken.property) {
      case 'field_name':
        this.agent.setFieldName(
          editFieldSettingsToken.fieldName,
          String(editFieldSettingsToken.value)
        );
        break;
      case 'field_type':
        this.handleFieldTypeSetting(editFieldSettingsToken);
        break;
      case 'allow_multiple_values':
        this.agent.setFieldMultiple(
          editFieldSettingsToken.fieldName,
          editFieldSettingsToken.value === 'true' || editFieldSettingsToken.value === true
        );
        break;
      case 'field_description':
        this.agent.setFieldDescription(
          editFieldSettingsToken.fieldName,
          String(editFieldSettingsToken.value)
        );
        break;
      case 'default_value':
        this.agent.setDefaultEnabled(editFieldSettingsToken.fieldName, true);
        this.valueProcessor.setCurrentEntryId(DEFAULT_ENTRY_ID);
        this.valueProcessor.processValueToken({
          tokenType: TokenType.VALUE,
          fieldName: editFieldSettingsToken.fieldName,
          value: editFieldSettingsToken.value,
          resetValue: true,
        });
        break;
      case 'number_format':
        this.handleNumberFormatSetting(editFieldSettingsToken);
        break;
      case 'depends_on':
        this.handleDependsOnSetting(editFieldSettingsToken, fieldId);
        break;
      default: {
        const errors = [
          {
            message: `Unknown property ${editFieldSettingsToken.property} for field ${editFieldSettingsToken.fieldName}`,
          },
        ];
        logValidationErrors(errors);
        this.addValidationErrors(errors);
        break;
      }
    }
  }

  private handleFieldTypeSetting(editFieldSettingsToken: EditFieldSettingToken): void {
    const fieldType = stringToDatabaseFieldType(String(editFieldSettingsToken.value));
    if (!fieldType) {
      const errors = [
        {
          message: `Unknown field type ${editFieldSettingsToken.value} for field ${editFieldSettingsToken.fieldName}`,
        },
      ];
      logValidationErrors(errors);
      this.addValidationErrors(errors);
      return;
    }

    if (OUT_OF_SCOPE_FIELD_TYPES.has(fieldType)) {
      const errors = [
        {
          message: `Field type ${editFieldSettingsToken.value} is not supported for field ${editFieldSettingsToken.fieldName}`,
          severity: 'warning',
        },
      ] as ProcessingError[];
      logValidationErrors(errors);
      this.addValidationErrors(errors);
      return;
    }

    this.agent.setFieldType(editFieldSettingsToken.fieldName, fieldType);
  }

  private handleNumberFormatSetting(editFieldSettingsToken: EditFieldSettingToken): void {
    const configValue = editFieldSettingsToken.value;

    if (
      typeof configValue === 'object' &&
      configValue &&
      'numberFormat' in configValue &&
      configValue.numberFormat
    ) {
      this.agent.setFieldNumberFormat(
        editFieldSettingsToken.fieldName,
        configValue.numberFormat
      );
    } else {
      const errors = [
        {
          message: `Invalid number format configuration "${configValue}" for field "${editFieldSettingsToken.fieldName}"`,
        },
      ];
      logValidationErrors(errors);
      this.addValidationErrors(errors);
    }
  }

  private handleDependsOnSetting(
    editFieldSettingsToken: EditFieldSettingToken,
    fieldId: string | undefined
  ): void {
    const dependentFieldName = String(editFieldSettingsToken.value);
    if (!dependentFieldName) {
      const errors = [
        {
          message: `Missing dependent field name for field ${editFieldSettingsToken.fieldName}`,
        },
      ];
      logValidationErrors(errors);
      this.addValidationErrors(errors);
      return;
    }

    const currentField = fieldId ? this.agent.getFieldById(fieldId) : undefined;
    if (currentField && !DEPENDENT_FIELD_TYPES.has(currentField.type)) {
      const errors = [
        {
          message: `Field "${editFieldSettingsToken.fieldName}" has type "${currentField.type}" which does not support dependencies`,
        },
      ];
      logValidationErrors(errors);
      this.addValidationErrors(errors);
      return;
    }

    const targetFieldId = this.agent.getFieldIdByName(dependentFieldName);
    const targetField = targetFieldId
      ? this.agent.getFieldById(targetFieldId)
      : undefined;
    if (targetField && OUT_OF_SCOPE_FIELD_TYPES.has(targetField.type)) {
      const errors = [
        {
          message: `Field "${dependentFieldName}" has type "${targetField.type}" which cannot be used as a dependency target`,
          severity: 'warning',
        },
      ] as ProcessingError[];
      logValidationErrors(errors);
      this.addValidationErrors(errors);
      return;
    }

    this.agent.setDependentFieldId(editFieldSettingsToken.fieldName, dependentFieldName);
  }

  public handleRemoveFieldToken(removeFieldToken: RemoveFieldToken): void {
    const fieldId = this.agent.getFieldIdByName(removeFieldToken.fieldName);
    if (!fieldId) {
      const errors = [
        {
          message: `Field not found: ${removeFieldToken.fieldName}. Cannot remove non-existent field.`,
        },
      ];
      logValidationErrors(errors);
      this.addValidationErrors(errors);
      return;
    }

    this.streamingState.scheduleFieldDeletion(fieldId, () => {
      this.agent.removeField(fieldId);
    });
  }

  private buildFieldSettings(
    fieldType: DatabaseFieldType,
    fieldToken: FieldToken
  ): Partial<DatabaseFieldSettings> | null {
    const settings: Partial<DatabaseFieldSettings> = {};

    if (fieldType === DatabaseFieldType.PAGE_LINK) {
      settings.creatable = false;
    }

    if (
      fieldType === DatabaseFieldType.PAGE_STATUS ||
      fieldType === DatabaseFieldType.PAGE_LABELS
    ) {
      settings.allowEdit = false;
    }

    if (fieldType === DatabaseFieldType.USER) {
      settings.mentionNotificationEnabled = false;
    }

    if (fieldType === DatabaseFieldType.JIRA_ISSUE) {
      settings.cloudId = this.cloudId;
    }

    if (fieldToken.configuration?.dependsOn && DEPENDENT_FIELD_TYPES.has(fieldType)) {
      const dependentFieldId = this.agent.getFieldIdByName(
        fieldToken.configuration.dependsOn
      );
      if (dependentFieldId) {
        settings.fieldId = dependentFieldId;
      }
    }

    if (
      fieldToken.configuration?.numberFormat &&
      fieldType === DatabaseFieldType.NUMBER
    ) {
      settings.numberFormat = fieldToken.configuration.numberFormat;
    }

    return Object.keys(settings).length > 0 ? settings : null;
  }

  private validateField(fieldToken: FieldToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!fieldToken.name) {
      errors.push({
        message: 'Field name is missing',
      });
    } else {
      const existingFieldId = this.agent.getFieldIdByName(fieldToken.name);
      if (existingFieldId) {
        errors.push({
          message: `Field with name "${fieldToken.name}" already exists. Subsequent operations will target the existing field.`,
          severity: 'warning',
        });
      }
    }

    if (!fieldToken.type) {
      errors.push({
        message: `Field type for field ${fieldToken.name} is missing`,
      });

      return errors;
    }

    const fieldType = stringToDatabaseFieldType(fieldToken.type);
    if (!fieldType) {
      errors.push({
        message: `Unsupported field type ${fieldToken.type} for field ${fieldToken.name}`,
      });
      return errors;
    }

    if (OUT_OF_SCOPE_FIELD_TYPES.has(fieldType)) {
      errors.push({
        message: `Field type ${fieldToken.type} is not yet supported`,
      });
      return errors;
    }

    if (DEPENDENT_FIELD_TYPES.has(fieldType)) {
      if (!fieldToken.configuration?.dependsOn) {
        errors.push({
          message: `Field ${fieldToken.name} with type ${fieldToken.type} is missing a dependsOn configuration.`,
          severity: 'warning',
        });
      } else {
        const dependentFieldId = this.agent.getFieldIdByName(
          fieldToken.configuration.dependsOn
        );

        if (!dependentFieldId) {
          errors.push({
            message: `Dependent field not found for field "${fieldToken.name}". Field will be created without reference.`,
            severity: 'warning',
          });
        }
      }
    } else if (fieldToken.configuration?.dependsOn) {
      errors.push({
        message: `Field "${fieldToken.name}" has dependsOn configuration but field type "${fieldToken.type}" does not support dependencies. Configuration will be ignored.`,
        severity: 'warning',
      });
    }

    return errors;
  }
}
